import type { AdReference, ResolvedAdReference } from "./ad-generation-request";

// Verified exact Properties record in both databases. Gulf Rentals is excluded.
export const TEMPLATE_PHOTO_BRAND_IDS: readonly string[] = [
  "0d57b57f-a192-4ea5-ba1e-b9fd0910e017",
];
export const MAX_TEMPLATE_PHOTO_REFERENCES = 12;
export const MAX_AD_IMAGE_SLOTS = 16;

export function templatePhotoEligibility(
  brandId: string | null | undefined,
  flags: { reference_images_enabled?: boolean; product_reference_library_enabled?: boolean },
): { enabled: boolean; libraryEnabled: boolean } {
  const enabled = Boolean(
    brandId && TEMPLATE_PHOTO_BRAND_IDS.includes(brandId) &&
    flags.reference_images_enabled === true,
  );
  return { enabled, libraryEnabled: enabled && flags.product_reference_library_enabled === true };
}

export function adReferenceCapacityError(refs: ReadonlyArray<{ purpose?: unknown }>, ordinaryLimit = 3): string | null {
  const photos = refs.filter((ref) => ref.purpose === "template_photo").length;
  if (photos > MAX_TEMPLATE_PHOTO_REFERENCES) return "You can attach up to 12 template photos. Remove the last selected photos and try again.";
  if (refs.length - photos > ordinaryLimit) return `You can attach up to ${ordinaryLimit} ordinary reference images total across all purposes. Remove ordinary references and try again.`;
  return null;
}

export function templatePhotoAdditionPurpose(offerMode: boolean, count: number): "auto" | "template_photo" {
  return offerMode && count >= 4 ? "template_photo" : "auto";
}

export function templatePhotoSourceUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return Array.from(new Set(raw.filter((url): url is string =>
    typeof url === "string" && url.trim().length > 0,
  ).map((url) => url.trim())));
}

// Append only after the complete legacy range. Accepted snapshots are never trimmed.
export function planTemplatePhotoSlots<T>(legacySlotCount: number, photos: ReadonlyArray<T>, engineLimit = MAX_AD_IMAGE_SLOTS) {
  const retained = photos.slice(0, Math.max(0, Math.min(MAX_AD_IMAGE_SLOTS, engineLimit) - legacySlotCount));
  return {
    retained,
    slots: retained.map((_, index) => legacySlotCount + index + 1),
    dropped: photos.length - retained.length,
    imageSlotsUsed: legacySlotCount + retained.length,
  };
}

export function composeTemplatePhotoPrompt(basePrompt: string, refs: ReadonlyArray<ResolvedAdReference>): string {
  const photos = refs.filter((ref) => ref.purpose === "template_photo");
  if (!photos.length) return basePrompt;
  const labels = photos.map((ref) => `Image ${ref.slot}`).join(", ");
  return `${basePrompt}\n\nTEMPLATE PHOTO SLOTS: The template image supplies layout, typography and color only, not photo content. Replace EVERY photo slot in the template with the listed photos in this order: ${labels}. Start with the first listed image. If there are more slots than photos, cycle from the first photo. If there are fewer slots, use only the first photos that fit and ignore the extras. Reproduce each photo as faithfully as possible: the same room, furniture, camera angle and lighting. Never invent photo content. Never keep any of the template's original photos. Never leave a photo slot as abstract shapes, color blocks or a placeholder. These are separate template photos, not additional views of the same featured product.`;
}

// Pure preview and render contract. Auto references remain ordinary references.
export function standardAdsExpressLegacySlotCount(args: {
  includeProduct: boolean;
  primaryProductPhoto: string;
  extraProductPhotoCount: number;
  includeLogo: boolean;
  includeFont: boolean;
  includePersonaCharacterSheet?: boolean;
  adIdentityPackCount?: number;
  references: ReadonlyArray<Pick<AdReference, "purpose">>;
}): number {
  const ordinary = args.references.filter((ref) => ref.purpose !== "template_photo");
  const productRef = ordinary.find((ref) => ref.purpose === "product" || ref.purpose === "product_environment");
  const occupied = args.includeProduct && Boolean(productRef || args.primaryProductPhoto);
  return 1 + Number(args.includeLogo) + Number(args.includeFont) + Number(occupied) +
    (args.includeProduct && !productRef ? args.extraProductPhotoCount : 0) +
    Number(args.includePersonaCharacterSheet === true) +
    (args.adIdentityPackCount === 3 ? 3 : 0) +
    ordinary.reduce((sum, ref) => sum + (ref.purpose === "person_identity" ? 2 : 1), 0) -
    Number(occupied && Boolean(productRef));
}
